import { useState } from 'react';
import {
  Badge, Button, Caption1, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Menu, MenuItem, MenuList,
  MenuPopover, MenuTrigger, MessageBar, MessageBarBody, Spinner, Tab, TabList, makeStyles, mergeClasses, tokens,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular, ArrowUploadRegular, CopyRegular, DocumentTextRegular, MoreHorizontalRegular, ProhibitedRegular, StarRegular,
} from '@fluentui/react-icons';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CellDate, CellMono, DataTable, EmptyState, PageHeader, TableStatusBadge } from '../common';
import type { DataTableColumn } from '../common';
import { useInstallations, type Installation } from '../deployments';
import {
  downloadRelease, formatBytes, makeCurrent as makeCurrentCall, type Release, type ReleaseChange, type ReleaseStatus,
} from '../../api/releases';
import { countInstalledOn, installBadge } from '../../releases/installCounts';
import { NO_SYNC_WARNINGS, describeSync, hasSyncWarnings, releaseErrorMessage, type SyncWarnings } from '../../releases/releaseChange';
import { SyncWarningBar } from './SyncWarningBar';
import { NotesDialog } from './NotesDialog';
import { PublishDrawer } from './PublishDrawer';
import { WithdrawDialog } from './WithdrawDialog';
import { useProducts, useReleaseList, useReleaseMutation } from './useReleases';

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  crumb: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, marginBottom: tokens.spacingVerticalS },
  muted: { color: tokens.colorNeutralForeground3 },
  notes: { maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  bad: { color: tokens.colorPaletteRedForeground1 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, justifyContent: 'flex-end', alignItems: 'center', whiteSpace: 'nowrap' },
  centred: { display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL },
});

function StatusPill({ status }: { status: ReleaseStatus }) {
  const color = status === 'Current' ? 'success' : status === 'Draft' ? 'informative' : status === 'Withdrawn' ? 'danger' : 'subtle';
  return <Badge appearance="tint" color={color}>{status}</Badge>;
}

export function ProductDetailPage() {
  const s = useStyles();
  const { productId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'installs' ? 'installs' : 'releases';
  const list = useReleaseList(productId);
  const products = useProducts();
  const installations = useInstallations();

  const [publishing, setPublishing] = useState(false);
  const [notesFor, setNotesFor] = useState<Release | null>(null);
  const [withdrawing, setWithdrawing] = useState<Release | null>(null);
  const [confirmCurrent, setConfirmCurrent] = useState<Release | null>(null);
  const [sync, setSync] = useState<SyncWarnings>(NO_SYNC_WARNINGS);
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const product = (products.data ?? []).find((p) => p.productId === productId);
  const data = list.data;
  const productName = data?.displayName ?? product?.displayName ?? productId;
  const guardianAppId = data?.guardianAppId ?? product?.guardianAppId ?? productId;
  const installs = installations.items.filter((i) => i.productId === guardianAppId);

  const makeCur = useReleaseMutation(productId, (version: string) => makeCurrentCall(productId, version));

  const noteChange = (r: ReleaseChange, text: string) => {
    setSync(describeSync(r));
    setBanner({ ok: true, text });
  };

  /** Tenants in an installed state running `version`; "2.3.6" from Guardian counts as release "2.3.6.0". */
  const installedOn = (version: string): number | null =>
    installations.error || installations.loading ? null : countInstalledOn(installs, version);

  if (list.isLoading) return <div className={s.centred}><Spinner label="Loading releases..." /></div>;
  if (list.error || !data) {
    return (
      <div>
        <PageHeader title={productName} />
        <MessageBar intent="error"><MessageBarBody>{(list.error as Error | null)?.message ?? 'Could not load this product.'}</MessageBarBody></MessageBar>
      </div>
    );
  }

  const columns: DataTableColumn<Release>[] = [
    { key: 'version', header: 'Version', render: (r) => <b style={{ fontFamily: 'Consolas, monospace' }}>{r.version}</b> },
    { key: 'status', header: 'Status', render: (r) => <StatusPill status={r.status} /> },
    { key: 'released', header: 'Released', render: (r) => <CellDate date={r.releasedAt} /> },
    { key: 'by', header: 'By', render: (r) => r.releasedBy || '—' },
    {
      key: 'installed', header: 'Installed on',
      render: (r) => { const n = r.status === 'Draft' ? null : installedOn(r.version); return n === null ? '—' : `${n} tenant${n === 1 ? '' : 's'}`; },
    },
    {
      key: 'notes', header: 'Notes',
      render: (r) => {
        const text = r.status === 'Withdrawn' ? `Withdrawn: ${r.withdrawnReason ?? ''}` : r.notes;
        return <div className={mergeClasses(s.notes, r.status === 'Withdrawn' && s.bad)} title={text}>{text}</div>;
      },
    },
    {
      key: 'package', header: 'Package',
      render: (r) => (<div><div>{formatBytes(r.sizeBytes)}</div><Caption1 className={s.muted}><CellMono>{r.sha256.slice(0, 8)}…</CellMono></Caption1></div>),
    },
    {
      key: 'actions', header: '', width: '1%',
      render: (r) => (
        <div className={s.actions}>
          {r.status === 'Draft' && (
            <Button size="small" icon={<StarRegular />} onClick={() => setConfirmCurrent(r)}>Make current</Button>
          )}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button size="small" appearance="subtle" icon={<MoreHorizontalRegular />} aria-label={`More actions for ${r.version}`} />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<DocumentTextRegular />} onClick={() => setNotesFor(r)}>View notes</MenuItem>
                <MenuItem
                  icon={<ArrowDownloadRegular />} disabled={r.status === 'Withdrawn'}
                  onClick={() => { downloadRelease(productId, r).catch((e) => setBanner({ ok: false, text: releaseErrorMessage(e) })); }}
                >
                  {r.status === 'Withdrawn' ? 'Download unavailable (withdrawn)' : `Download .sppkg (${formatBytes(r.sizeBytes)})`}
                </MenuItem>
                <MenuItem
                  icon={<CopyRegular />}
                  onClick={() => {
                    navigator.clipboard.writeText(r.sha256).then(
                      () => setBanner({ ok: true, text: `sha256 for ${r.version} copied.` }),
                      () => setBanner({ ok: false, text: 'The browser blocked copying. Open View notes to select the hash.' }),
                    );
                  }}
                >
                  Copy sha256
                </MenuItem>
                {(r.status === 'Current' || r.status === 'Superseded') && (
                  <MenuItem icon={<ProhibitedRegular />} onClick={() => setWithdrawing(r)}>Withdraw…</MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      ),
    },
  ];

  const withdrawnVersions = data.releases.filter((r) => r.status === 'Withdrawn').map((r) => r.version);
  const installColumns: DataTableColumn<Installation>[] = [
    { key: 'customer', header: 'Customer', render: (i) => <b>{i.customerName}</b> },
    { key: 'site', header: 'Site', render: (i) => i.siteUrl || '—' },
    {
      key: 'running', header: 'Running',
      render: (i) => {
        const badge = installBadge(i, data.current, withdrawnVersions);
        return (
          <span>
            <CellMono>{i.version || '—'}</CellMono>{' '}
            {badge?.kind === 'withdrawn' ? <Badge appearance="tint" color="danger">withdrawn build</Badge>
              : badge?.kind === 'update' ? <Badge appearance="tint" color="warning">{badge.target ? `→ ${badge.target} available` : 'outdated'}</Badge> : null}
          </span>
        );
      },
    },
    { key: 'latest', header: 'Latest', render: (i) => <CellMono>{i.latestVersion ?? data.current ?? '—'}</CellMono> },
    { key: 'state', header: 'State', render: (i) => <TableStatusBadge status={i.state} /> },
    { key: 'updated', header: 'Updated', render: (i) => <CellDate date={i.updatedAt} /> },
  ];

  return (
    <div>
      <div className={s.crumb}><Link to="/products">Products</Link> / {productName}</div>
      <PageHeader
        title={productName}
        description={`Solution id ${product?.package.solutionId ?? data.releases[0]?.solutionId ?? 'unknown'}`}
        actions={<Button appearance="primary" icon={<ArrowUploadRegular />} onClick={() => setPublishing(true)}>Publish new release</Button>}
      />
      <div className={s.stack}>
        {hasSyncWarnings(sync) && (
          <SyncWarningBar
            productId={productId} warnings={sync}
            onResynced={(left, text, ok) => { setSync(left); setBanner({ ok, text }); }}
          />
        )}
        {banner && (
          <MessageBar intent={banner.ok ? 'success' : 'error'}>
            <MessageBarBody>{banner.text}</MessageBarBody>
          </MessageBar>
        )}
        <TabList selectedValue={tab} onTabSelect={(_, d) => setParams(d.value === 'installs' ? { tab: 'installs' } : {}, { replace: true })}>
          <Tab value="releases">Releases</Tab>
          <Tab value="installs">Installs{installations.loading ? '' : ` (${installs.length})`}</Tab>
        </TabList>

        {tab === 'releases' ? (
          <>
            {data.releases.length === 0 ? (
              <EmptyState title="No releases yet" description="Publish the first release by uploading a .sppkg." action={{ label: 'Publish new release', onClick: () => setPublishing(true) }} />
            ) : (
              <DataTable columns={columns} data={data.releases} keyExtractor={(r) => r.version} />
            )}
            <Caption1 className={s.muted}>Lifecycle: Draft → Current → Superseded (installable) → Withdrawn (cannot be installed).</Caption1>
          </>
        ) : installations.loading ? (
          <div className={s.centred}><Spinner label="Loading installs..." /></div>
        ) : installations.error ? (
          <MessageBar intent="error"><MessageBarBody>{installations.error}</MessageBarBody></MessageBar>
        ) : installs.length === 0 ? (
          <EmptyState title="Not installed anywhere yet" description="Customer installs of this product appear here once Guardian records them." />
        ) : (
          <DataTable columns={installColumns} data={installs} keyExtractor={(i) => `${i.tenantId}:${i.productId}`} onRowClick={undefined} />
        )}
      </div>

      {publishing && (
        <PublishDrawer
          productId={productId} productName={productName} currentVersion={data.current} releases={data.releases}
          onClose={() => setPublishing(false)}
          onPublished={(r, version, madeCurrent) => {
            setPublishing(false);
            setParams({}, { replace: true });
            noteChange(r, madeCurrent ? `${productName} ${version} is now current.` : `${productName} ${version} saved as a draft.`);
          }}
        />
      )}
      {notesFor && <NotesDialog productName={productName} release={notesFor} onClose={() => setNotesFor(null)} />}
      {withdrawing && (
        <WithdrawDialog
          productId={productId} productName={productName} release={withdrawing} releases={data.releases}
          installedOn={installedOn(withdrawing.version)}
          onClose={() => setWithdrawing(null)}
          onDone={(r) => {
            const v = withdrawing.version;
            setWithdrawing(null);
            noteChange(r, `${v} withdrawn.${r.promoted ? ` ${r.promoted} is now current.` : ''}`);
          }}
        />
      )}
      {confirmCurrent && (
        <Dialog open onOpenChange={(_, d) => { if (!d.open && !makeCur.isPending) { setConfirmCurrent(null); makeCur.reset(); } }}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Make {productName} {confirmCurrent.version} current?</DialogTitle>
              <DialogContent>
                {data.current ? `${data.current} becomes Superseded and stays installable. ` : ''}
                Nothing is installed yet: new deployment sessions will use this release.
                {makeCur.error && <MessageBar intent="error" style={{ marginTop: 12 }}><MessageBarBody>{releaseErrorMessage(makeCur.error)}</MessageBarBody></MessageBar>}
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" disabled={makeCur.isPending} onClick={() => { setConfirmCurrent(null); makeCur.reset(); }}>Cancel</Button>
                <Button
                  appearance="primary" disabled={makeCur.isPending}
                  onClick={() => makeCur.mutate(confirmCurrent.version, {
                    onSuccess: (r) => { const v = confirmCurrent.version; setConfirmCurrent(null); noteChange(r, `${productName} ${v} is now current.`); },
                  })}
                >
                  {makeCur.isPending ? 'Working…' : 'Make current'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
}
